
import React, { MouseEvent, useState } from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box, Checkbox, TablePagination } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { useTranslate } from 'react-admin';
import { useLocation } from "react-router-dom";
import { Clear, DeleteOutline } from '@material-ui/icons';
import { CustomTableProps, EnhancedTableHeadProps, ExternalDevice, Order, OrderSort } from '../../types';
import HeaderSortable from '../header-sortable';
import SnackbarCustom from '../../users/snackbar';
import { StyledButton } from '.';
import { cloneDeep } from 'lodash';

const StyledTableCell = withStyles((theme) => ({
    head: {
        backgroundColor: theme.palette.primary.main,
        borderBottom: 'solid 5px ' + theme.palette.background.default,
        fontWeight: 'inherit'
    },
    body: {
        fontSize: 14,
    },
}))(TableCell);

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
    },
    container: {
        maxHeight: 600,
    },
    tableCell: {
        borderBottom: 'solid 5px ' + theme.palette.background.default
    },
    visuallyHidden: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
        color: 'white'
    },
    tableSortLabel: {
        fontFamily: 'UniviaPro',
        fontSize: ' 13px',
        fontWeight: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: 1.25,
        letterSpacing: ' 0.13px',
        color: '#ffffff',
        '&:hover': {
            color: '#ffffff'
        },
        '&:focus': {
            color: '#ffffff'
        },
    },
    textTableName: {
        fontFamily: 'UniviaPro-Book',
        fontSize: '13px',
        fontWeight: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: 'normal',
        letterSpacing: 'normal',
        color: '#237fc2',
    },
    textTable: {
        fontFamily: 'UniviaPro-Book',
        fontSize: '13px',
        fontWeight: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: 'normal',
        letterSpacing: 'normal',
        color: '#4f5064',
        width: 280,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap'
    },
    textTableSmaller: {
        fontFamily: 'UniviaPro-Book',
        fontSize: '13px',
        fontWeight: 'normal',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: 'normal',
        letterSpacing: 'normal',
        color: '#4f5064',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap'
    },
    borderCluster: {
        borderRadius: '10px',
        border: 'solid 1px #e0f0f2',
        backgroundColor: ' #fafefe',
    },
    textCluster: {
        fontFamily: 'UniviaPro-Bold',
        fontSize: '9px',
        fontWeight: 'bold',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: 1.22,
        letterSpacing: ' 0.36px',
        color: '#6cb5b8',
        textAlign: 'center',
        padding: '8px',
    },
    textEdit: {
        fontFamily: 'UniviaPro',
        fontSize: '9px',
        fontWeight: 'bold',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: 1.22,
        letterSpacing: '0.36px',
        color: '#ffffff',
    },
    borderDelete: {
        borderRadius: '12px',
        backgroundColor: '#e5f1f8',
    },

    TitleUser: {
        fontFamily: 'UniviaPro-Bold',
        fontSize: '18px',
        fontWeight: 500,
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: 1.2,
        letterSpacing: 'normal',
        color: '#4f5064',
    },
    textbtnCreate: {
        fontFamily: 'UniviaPro-Bold',
        fontSize: '9px',
        fontWeight: 'bold',
        fontStretch: 'normal',
        fontStyle: 'normal',
        lineHeight: 1.22,
        letterSpacing: '0.36px',
        color: '#ffffff',
    },
    large: {
        border: '1px solid #ddd',
        borderRadius: 2,
        padding: 2,
        width: 50,
        height: 40,
        '&:hover': {
            boxShadow: '0 0 2px 1px rgba(0, 140, 186, 0.5)'
        }
    },
}));

function EnhancedTableHead({ classes, order, setOrder, handleCheckMark, data, checked, headerCell }: EnhancedTableHeadProps) {
    console.log('###order', order);
    return (
        <TableHead>
            <TableRow>
                {headerCell.map((headCell) => (
                    <StyledTableCell
                        className={classes.tableSortLabel}
                        key={'cell-' + headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                    >
                        {
                            headCell.label !== '' ?
                                (
                                    headCell.sortable
                                        ? <HeaderSortable
                                            title={headCell.label}
                                            field={headCell.id}
                                            order={order}
                                            setOrder={setOrder}
                                            hiddenIconSort={undefined} />
                                        : headCell.label
                                )
                                :
                                headCell.id === 'checkmark' ?
                                    <Box>
                                        <Checkbox
                                            checked={checked.length === data.length}
                                            color='primary'
                                            onChange={(event) => handleCheckMark(event, -1, true)}
                                        />
                                    </Box>
                                    :
                                    <Box />

                        }
                    </StyledTableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

function CustomTable({ data, getList, onDelete, total, onClear, checked, handleCheckMark, error, headerCell, setStateExternalDevice, setItemRow }: CustomTableProps) {
    const clone = cloneDeep(headerCell);

    const classes = useStyles();
    const translate = useTranslate();
    const location = useLocation<any>();

    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    // const [order, setOrder] = useState<Order>({ field: 'name', order: 'ASC' });
    const [order, setOrder] = useState<OrderSort>({ field: 'name', order: 'ASC' });
    const [openEdit, setOpenEdit] = useState<boolean>(false);

    const handleChangePage = (newPage: any) => {
        const pagination = { page: newPage + 1, perPage: rowsPerPage };
        getList(order, pagination);
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: { target: { value: string; }; }) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        const pagination = { page: 1, perPage: Number(event.target.value) };
        getList(order, pagination);
    };

    const handleRequestSort = (nextSort: OrderSort) => {
        setPage(0);
        setOrder(nextSort);

        const pagination = { page: 1, perPage: rowsPerPage };
        getList(nextSort, pagination);
    };

    const handleCloseEdit = () => {
        setOpenEdit(!openEdit);
    };

    const handleClickItem = (event: MouseEvent<HTMLElement>) => (item: ExternalDevice) => {
        if (event.target && (event.target as any).getAttribute("type") === 'checkbox') {
            return
        } else {
            setStateExternalDevice && setStateExternalDevice(1)
            setItemRow && setItemRow(item)
        }
    }

    return (
        <Paper className={classes.root}>
            {
                location.state !== undefined && location.state.user !== undefined &&
                <SnackbarCustom
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right"
                    }}
                    message="User Deleted !"
                    open={location.state.open === "true" ? !openEdit : openEdit}
                    handleClose={() => handleCloseEdit()} />
            }

            <TableContainer className={classes.container}>
                <Table aria-label="sticky table">
                    {
                        checked.length > 0 ? (
                            <TableHead style={{ maxHeight: 70 }}>
                                <TableRow style={{ maxHeight: 70 }}>
                                    <TableCell style={{ backgroundColor: '#56657f', padding: '17px' }}>
                                        <Checkbox checked={checked.length === data.length} color='primary' onChange={(event) => handleCheckMark(event, -1, true)} />
                                    </TableCell>
                                    <TableCell style={{ backgroundColor: '#56657f', padding: '16px', height: '14%' }}>
                                        <StyledButton
                                            onClick={onDelete}
                                            variant='text'
                                            startIcon={<DeleteOutline />}
                                            style={{ left: -50, width: 68 }}
                                        >{translate(`pos.common.delete`)}</StyledButton>
                                    </TableCell>
                                    {
                                        clone && clone.length > 0 && clone.splice(0, clone.length - 3).map((item, index) => {
                                            return (
                                                <TableCell key={index} style={{ backgroundColor: '#56657f', padding: '16px', height: '14%', width: 70 }}></TableCell>
                                            )
                                        })
                                    }
                                    <TableCell style={{ backgroundColor: '#56657f', padding: '8px 0', height: '14%' }} align='right'>
                                        <StyledButton
                                            onClick={onClear}
                                            variant='text'
                                            startIcon={<Clear />}
                                        >{checked.length} {checked.length > 1 ? translate('pos.categories.filesSelected') : translate('pos.categories.fileSelected')}</StyledButton>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                        ) :
                            < EnhancedTableHead
                                checked={checked}
                                data={data}
                                handleCheckMark={handleCheckMark}
                                classes={classes}
                                order={order}
                                setOrder={handleRequestSort}
                                headerCell={headerCell}
                            />
                    }
                    {
                        error > 0 ?
                            <TableBody>
                                {
                                    data.map((item: any, index: number) => (
                                        <>
                                            <TableRow key={'table-row-' + index} tabIndex={-1} style={{ cursor: 'pointer' }} onClick={(event) => handleClickItem(event)(item)}>
                                                <TableCell key={index + '-0'} style={{ width: 60, height: 48 }} className={classes.tableCell}>
                                                    <Checkbox checked={item.checked ? true : false} onChange={(event) => handleCheckMark(event, index)} color='primary' />
                                                </TableCell>
                                                <TableCell key={index + '-1'} className={classes.tableCell}><Typography className={classes.textTableSmaller} >{item.deviceId}</Typography></TableCell>
                                                <TableCell key={index + '-2'} className={classes.tableCell}><Typography className={classes.textTableSmaller} >{item.macAddressBox}</Typography></TableCell>
                                            </TableRow>
                                        </>
                                    ))
                                }

                            </TableBody> :
                            <TableBody>
                                <TableRow>
                                    <TableCell className={classes.tableCell}></TableCell>
                                    <TableCell className={classes.tableCell}></TableCell>
                                    <TableCell className={classes.tableCell}> <Typography className={classes.textTable}>{translate(`pos.channels.noData`)}</Typography></TableCell>
                                </TableRow>
                            </TableBody>
                    }
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={total || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onChangePage={handleChangePage}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                onPageChange={handleChangePage}
            />
        </Paper>
    );
}

export default CustomTable;
